/**
 * Pick Name — canonical resolver and write-boundary normaliser.
 *
 * A reel option's player-facing label is `displayNameOverride` if a meaningful
 * override is set, otherwise `outcomeName`. The override is stored on
 * `reel_options.display_name_override` and is never read directly by player
 * surfaces — they consume the resolved `pickName` from the backend.
 *
 * See CONTEXT.md → "Pick Name".
 */
/**
 * Normalise an override input at the write boundary: trim, then collapse
 * empty / whitespace-only strings to `null`. Use this on every write path
 * that accepts `displayNameOverride` from a request body so blank strings
 * can never enter storage.
 */
export const normalisePickNameOverride = (input) => {
    if (input == null)
        return null;
    const trimmed = input.trim();
    return trimmed === '' ? null : trimmed;
};
/**
 * A pick named after a club, shortened to that club's abbreviation (CAE-1651):
 * `Milwaukee Bucks` becomes `MIL`, and `Milwaukee Bucks -5.5` becomes
 * `MIL -5.5`, so a handicap or a total survives the shortening.
 *
 * The club is whichever side of the fixture the name starts with — the same
 * test that put the club on the option when it was added — so this needs no
 * market kind to be told what it is looking at. A player pick is named
 * `Over 27.5`, matches neither side, and comes back untouched.
 *
 * Longest name first: one club's name can be the start of another's, and the
 * longer match is the right one.
 */
const abbreviateClubName = (outcomeName, clubs) => {
    const name = outcomeName.trim();
    const lowered = name.toLowerCase();
    const sides = clubs
        .filter((club) => club.name.trim() !== '' && (club.abbreviation ?? '').trim() !== '')
        .sort((a, b) => b.name.trim().length - a.name.trim().length);
    for (const side of sides) {
        const club = side.name.trim();
        const abbreviation = (side.abbreviation ?? '').trim();
        const key = club.toLowerCase();
        if (lowered === key)
            return abbreviation;
        if (lowered.startsWith(`${key} `))
            return `${abbreviation}${name.slice(club.length)}`;
    }
    return outcomeName;
};
/**
 * Resolve a Pick Name from a record carrying both the override and the
 * canonical outcome name. Returns the override when meaningful, otherwise the
 * outcome name. Defends against blank/whitespace overrides reaching the read
 * path — the DB column is unconstrained, so historical rows or manual edits
 * can still hold `''` or `'   '` even though current write paths normalise.
 *
 * Given the fixture's `clubs`, a pick named after one of them is shortened to
 * its abbreviation. **An override always wins**, and is never shortened: this
 * is the one place that can see both halves, so it is the only place that can
 * tell the feed's name from a name an Operator wrote. Callers with no club to
 * offer — Admin, which shows the pair, and the award payload, which must send
 * Caesars the name they gave us — leave `clubs` out and are unaffected.
 */
export const resolvePickName = (record) => {
    const override = normalisePickNameOverride(record.displayNameOverride);
    if (override !== null)
        return override;
    const clubs = record.clubs ?? [];
    return clubs.length === 0 ? record.outcomeName : abbreviateClubName(record.outcomeName, clubs);
};
//# sourceMappingURL=pick-name.js.map